import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, Loader2, Scale } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { PRACTICE_CASE_TYPES } from "@/constants/practice";

const MediationBriefPage = () => {
  const [form, setForm] = useState({
    case_type: 'personal_injury',
    jurisdiction: 'us_federal',
    case_summary: '',
    client_position: 'plaintiff',
    key_facts: [],
    damages: {}
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/practice/mediation-brief`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="mediation-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Mediation Briefs</h1>
          <p className="text-muted-foreground">Generate compelling mediation position statements</p>
        </div>
        
        {!result ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader><CardTitle>Mediation Details</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Case Type</Label>
                      <Select value={form.case_type} onValueChange={v => setForm(f => ({...f, case_type: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{PRACTICE_CASE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Client Position</Label>
                      <Select value={form.client_position} onValueChange={v => setForm(f => ({...f, client_position: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="plaintiff">Plaintiff</SelectItem>
                          <SelectItem value="defendant">Defendant</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Case Summary</Label>
                    <Textarea placeholder="Describe the case, parties, and key issues..." value={form.case_summary} onChange={e => setForm(f => ({...f, case_summary: e.target.value}))} rows={4} />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card className="bg-teal-600 text-white border-0">
              <CardContent className="p-6">
                <Button className="w-full h-14 text-lg gap-2 bg-white text-teal-700 hover:bg-white/90" onClick={generate} disabled={loading || !form.case_summary}>
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Generating...</> : <><Scale className="h-5 w-5" /> Generate Brief</>}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            <Card className="bg-teal-600 text-white border-0">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold">Mediation Position Statement</h2>
                <p className="text-white/80">{result.case_type} • {result.client_position}</p>
                <Button variant="outline" onClick={() => setResult(null)} className="mt-4 text-white border-white/20">New Brief</Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">{result.content}</div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default MediationBriefPage;
